import {
	Component,
	HostListener,
	Input,
	OnInit,
	ViewChild,
} from '@angular/core';

import {
	ChartConfiguration,
	ChartData,
	ChartDataset,
	ChartType,
} from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';

import { ChartDataModel } from './models/chart-data.model';

@Component({
	selector: 'app-chart',
	styleUrls: ['./chart.component.scss'],
	templateUrl: './chart.component.html',
})
export class ChartComponent implements OnInit {
	@ViewChild(BaseChartDirective) chart: BaseChartDirective | undefined;

	@Input() data: ChartDataModel | undefined;

	@Input() hideLegend: boolean = false;
	chartData: ChartData<'bar'> | undefined;

	chartOptions: ChartConfiguration['options'];
	chartPlugins = [
		{
			id: 'custom_canvas_background_color',
			beforeDraw: (chart: any) => {
				const ctx = chart.canvas.getContext('2d');
				ctx.save();
				ctx.globalCompositeOperation = 'destination-over';
				ctx.fillStyle = '#ffffff';
				ctx.fillRect(0, 0, chart.width, chart.height);
				ctx.restore();
			},
		},
	];
	chartType: ChartType = 'bar';

	isMobile: boolean;

	constructor() {
		this.isMobile = window.innerWidth < 768;
	}

	@HostListener('window:resize', ['$event'])
	resize() {
		if (this.isMobile !== window.innerWidth < 769) {
			this.isMobile = !this.isMobile;
			this.processChart();
		}
	}

	ngOnInit(): void {
		if (this.data) {
			this.processChart();
		}
	}

	processChart(): void {
		this.chartData = this.processChartData();
		this.chartOptions = this.processChartOptions();
		this.chart?.update();
	}

	processChartData(): ChartData<'bar'> {
		const colors: string[] = [
			'#7F8BEE',
			'#6ED1F7',
			'#44F0D1',
			'#DD7BED',
			'#F9A860',
		];
		let completeDatasets = this.data!.datasets.map((d, i) => {
			const colorIdx = i < 5 ? i : i % 5;

			return {
				backgroundColor: colors[colorIdx],
				data: d.data,
				hoverBackgroundColor: colors[colorIdx],
				label: d.label,
			};
		});

		if (this.isMobile) {
			const labels: string[] = this.data!.labels.slice(
				this.data!.labels.length - 2,
			);
			const datasets: ChartDataset<'bar', number[]>[] = completeDatasets.map(
				(d) => {
					return {
						...d,
						data: d.data.slice(d.data.length - 2),
					};
				},
			);

			return {
				datasets,
				labels,
			};
		}

		return {
			datasets: completeDatasets,
			labels: this.data!.labels,
		};
	}

	processChartOptions(): ChartConfiguration['options'] {
		const values: number[] = [];
		this.chartData!.datasets.forEach((d) =>
			d.data.forEach((v) => values.push(v)),
		);

		// Encontra o maior valor e arredonda para o próximo milhar
		const max = Math.ceil(Math.max(...values) / 1000) * 1000;

		return {
			animation: false,
			maintainAspectRatio: false,
			plugins: {
				legend: {
					display: !this.hideLegend,
					labels: {
						boxWidth: 12,
					},
					position: 'bottom',
				},
				tooltip: {
					callbacks: {
						label: function (ctx) {
							return `R$ ${ctx.formattedValue
								.replace(/,/g, 'a')
								.replace(/\./g, ',')
								.replace(/a/g, '.')}`;
						},
					},
					displayColors: false,
				},
			},
			responsive: true,
			scales: {
				x: {
					grid: {
						display: false,
						drawBorder: false,
					},
				},
				y: {
					grid: {
						borderDash: [3, 4],
						color: '#E5E7EB',
						drawBorder: false,
						drawTicks: false,
					},
					max, // Aplica o valor arredondado para o próximo milhar
					min: 0,
					ticks: {
						callback: function (value) {
							return `R$ ${value}`;
						},
					},
				},
			},
		};
	}
}
